import { useEffect, useState } from 'react';
import { Copy, Link2 } from 'lucide-react';
import { Button, Modal, ModalHeader, useToast } from './ui';
import { createClass, notifyClassesChanged } from '../data/api';
import { useAuth } from '../../Accounts/AuthContext';
import { JOIN_LIVE, joinLink } from '../../Join/joinLinks';

// Preset class colors (dot in the switcher and rosters). Same family as the
// mock class colors so existing classes keep reading as one set.
const CLASS_COLOR_PRESETS = ['#3b82f6', '#8F00FF', '#16a34a', '#f59e0b', '#e03a3a', '#0ea5e9'];

const NEW_CLASS_INPUT =
  'h-9 w-full rounded-[6px] border border-neutral-300 bg-white px-2.5 text-[13px] text-ink outline-none focus:border-primary-400';

// New class flow (2026-08-04): create via claude-teacher-api createClass, then
// show the returned join code prominently. On success the module class cache is
// already invalidated (createClass wrapper), so notifying listeners re-reads it
// and selecting the new id lands every page on the new class.
// Exported for Buffy's newClass action (2026-10-13): initialName prefills the
// name the teacher gave in chat; onCreated(class) reports the new class.
// holdUntilDone (2026-10-28, the first-class screen): the class list refreshes
// only when the teacher closes the dialog. The first class ends the first-run
// screen that holds this dialog, so an early refresh closed it before the
// teacher saw the code and the invite link.
export default function NewClassModal({ open, onClose, selectClass, initialName = '', initialPeriod = '', onCreated, holdUntilDone = false }) {
  const { user, handleAPIResponse } = useAuth();
  const pushToast = useToast();
  const [name, setName] = useState('');
  const [period, setPeriod] = useState('');
  const [color, setColor] = useState(CLASS_COLOR_PRESETS[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null); // API class row (has joinCode)

  useEffect(() => {
    if (!open) return;
    setName(initialName || ''); setPeriod(initialPeriod || '');
    setColor(CLASS_COLOR_PRESETS[0]);
    setBusy(false); setError(null); setCreated(null);
    // Prefill only when the dialog opens; later prop changes never reset typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    if (busy || !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      // School and SAT date dropped from the form (Sandy 2026-08-08): the
      // teacher's profile school scopes everything school-related now.
      const fields = { name: name.trim(), color };
      if (period.trim()) fields.period = period.trim();
      const c = await createClass({ user, handleAPIResponse }, fields);
      setCreated(c || {});
      if (!holdUntilDone) showNewClass(c);
      onCreated?.({ ...(c || {}), name: fields.name, period: fields.period || '' });
    } catch (e) {
      setError(e.message || 'Could not create the class');
    } finally {
      setBusy(false);
    }
  };

  // Refresh the class list and land every page on the new class.
  function showNewClass(c) {
    notifyClassesChanged();
    const id = c?.classID || c?.id;
    if (id && selectClass) selectClass(id);
  }
  const close = () => {
    if (holdUntilDone && created) showNewClass(created);
    onClose();
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(created.joinCode);
      pushToast(`Join code ${created.joinCode} copied`);
    } catch {
      pushToast('Could not copy; the code is shown here');
    }
  };

  // The class link (2026-10-27): the student signup with the code filled in.
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinLink(created.joinCode));
      pushToast('Invite link copied');
    } catch {
      pushToast('Could not copy the link');
    }
  };

  return (
    <Modal open={open} onClose={close} size="sm">
      <ModalHeader
        title={created ? 'Class created' : 'New class'}
        sub={created ? null : 'Set it up; students join with a code.'}
        onClose={close}
      />
      {created ? (
        <>
          <div className="px-[24px] py-6 text-center">
            <div className="text-[11px] font-semibold text-neutral-500">Students join with this code</div>
            {created.joinCode ? (
              <div className="mt-2 flex items-center justify-center gap-2.5">
                <span className="text-[28px] font-extrabold tracking-[0.12em] text-ink tabular-nums">{created.joinCode}</span>
                <button
                  type="button"
                  onClick={copyCode}
                  title="Copy join code"
                  className="flex items-center gap-1 px-2 py-1.5 rounded-[6px] border-[1px] border-neutral-300 bg-white text-neutral-500 hover:text-ink hover:bg-neutral-50 cursor-pointer"
                >
                  <Copy size={14} strokeWidth={2} />
                  <span className="text-[12px] font-semibold">Copy</span>
                </button>
              </div>
            ) : (
              <p className="mt-2 mb-0 text-[13px] text-neutral-600">The class is ready. Its join code is in the class switcher.</p>
            )}
            {created.joinCode && JOIN_LIVE && (
              <div className="mt-4 text-left">
                <div className="text-[11px] font-semibold text-neutral-500 text-center">Or send them this link</div>
                <div className="mt-1.5 flex items-center gap-2 rounded-[8px] border-[1px] border-neutral-300 bg-neutral-50 pl-3 pr-1 py-1">
                  <span className="flex-1 min-w-0 truncate text-[12.5px] text-ink" title={joinLink(created.joinCode)}>{joinLink(created.joinCode)}</span>
                  <button
                    type="button"
                    onClick={copyLink}
                    title="Copy the invite link"
                    className="flex-none flex items-center gap-1 px-2 py-1.5 rounded-[6px] border-[1px] border-neutral-300 bg-white text-neutral-500 hover:text-ink hover:bg-neutral-50 cursor-pointer"
                  >
                    <Link2 size={14} strokeWidth={2} />
                    <span className="text-[12px] font-semibold">Copy link</span>
                  </button>
                </div>
              </div>
            )}
            <p className="mt-[12px] mb-0 text-[12px] text-neutral-500 leading-relaxed">
              {JOIN_LIVE
                ? 'The link opens the student signup with the code filled in. Both also live in the class switcher.'
                : 'Read it aloud or paste it wherever your class will see it. It also lives in the class switcher.'}
            </p>
          </div>
          <div className="flex items-center justify-end gap-2 px-[24px] py-3.5 border-t border-neutral-200 bg-neutral-50 flex-none">
            <Button variant="primary" size="sm" onClick={close}>Done</Button>
          </div>
        </>
      ) : (
        <>
          <div className="px-[24px] py-[16px] flex flex-col gap-[12px]">
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold text-neutral-500">Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Algebra II" className={NEW_CLASS_INPUT} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold text-neutral-500">Period</span>
              <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="Period 3" className={NEW_CLASS_INPUT} />
            </label>
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold text-neutral-500">Color</span>
              <div className="flex items-center gap-2">
                {CLASS_COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    aria-label={`Class color ${c}`}
                    aria-pressed={color === c}
                    className={
                      'w-7 h-7 rounded-[6px] cursor-pointer border-2 transition-colors ' +
                      (color === c ? 'border-ink' : 'border-transparent hover:border-neutral-300')
                    }
                    style={{ background: c }}
                  />
                ))}
              </div>
            </div>
            {error && <div className="text-[12px] text-error-700">{error}</div>}
          </div>
          <div className="flex items-center justify-end gap-2 px-[24px] py-3.5 border-t border-neutral-200 bg-neutral-50 flex-none">
            <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={submit} disabled={busy || !name.trim()}>
              {busy ? 'Creating…' : 'Create class'}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
